import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { getCollectionsByOrganizer, getParticipatingCollections, deleteCollection } from "@/services/collections";
import { getMessageCountsForCollections } from "@/services/messages";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Plus, CircleDollarSign, SlidersHorizontal, X, Check, Trash2, Loader2, LayoutGrid, Layers, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AppHeader from "@/components/AppHeader";
import type { Tables } from "@/integrations/supabase/types";
import { useCurrency } from "@/hooks/useCurrency";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const OCCASION_OPTIONS: { value: string; key: string; emoji: string }[] = [
  { value: "birthday", key: "birthday", emoji: "🎂" },
  { value: "farewell", key: "farewell", emoji: "👋" },
  { value: "baby", key: "newBaby", emoji: "👶" },
  { value: "wedding", key: "wedding", emoji: "💍" },
  { value: "thank-you", key: "thankYou", emoji: "🙏" },
  { value: "retirement", key: "retirement", emoji: "🎉" },
  { value: "get-well", key: "getWell", emoji: "💐" },
  { value: "sympathy", key: "sympathy", emoji: "🕊️" },
  { value: "graduation", key: "graduation", emoji: "🎓" },
  { value: "teacher", key: "teacher", emoji: "📚" },
  { value: "anniversary", key: "anniversary", emoji: "❤️" },
  { value: "christmas", key: "christmas", emoji: "🎄" },
  { value: "valentines", key: "valentines", emoji: "💝" },
  { value: "congratulations", key: "congratulations", emoji: "🏆" },
];

const STATUS_FILTER_OPTIONS: { value: "active" | "draft" | "closed"; key: string }[] = [
  { value: "active", key: "active" },
  { value: "draft", key: "draft" },
  { value: "closed", key: "closed" },
];

const TYPE_FILTER_OPTIONS: { value: "card_only" | "card_collection"; key: string; emoji: string }[] = [
  { value: "card_only", key: "card_only", emoji: "💌" },
  { value: "card_collection", key: "card_collection", emoji: "🎁" },
];

type FilterCategory = "status" | "occasion" | "type";

const cardTypes = [
  { emoji: "🎂", label: "Birthday" },
  { emoji: "💍", label: "Wedding" },
  { emoji: "👶", label: "New Baby" },
  { emoji: "🎓", label: "Graduation" },
  { emoji: "👋", label: "Farewell" },
  { emoji: "🙏", label: "Thank You" },
  { emoji: "🎉", label: "Retirement" },
  { emoji: "❤️", label: "Anniversary" },
  { emoji: "🎄", label: "Christmas" },
  { emoji: "💝", label: "Valentine's" },
  { emoji: "🏆", label: "Congrats" },
];

const FlippingCardTypes = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % cardTypes.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex justify-center mb-6" style={{ perspective: "600px" }}>
      <div className="relative w-28 h-36">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ rotateY: 90, opacity: 0 }}
            animate={{ rotateY: 0, opacity: 1 }}
            exit={{ rotateY: -90, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="absolute inset-0 rounded-2xl bg-card border border-border/60 shadow-md flex flex-col items-center justify-center gap-2"
            style={{ backfaceVisibility: "hidden" }}
          >
            <span className="text-4xl">{cardTypes[index].emoji}</span>
            <span className="text-xs font-medium text-muted-foreground font-sans">{cardTypes[index].label}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Narrow shape covering only the columns the dashboard grid reads.
// Mirrors the slim select in services/collections.ts so the query
// result type matches this without needing the full Row.
type DashboardCollection = Pick<
  Tables<"collections">,
  | "id"
  | "organizer_id"
  | "title"
  | "recipient_name"
  | "share_slug"
  | "status"
  | "product_image"
  | "product_name"
  | "total_collected"
  | "type"
  | "pool_type"
  | "occasion"
  | "created_at"
> & {
  message_count?: number;
  template_image_url?: string | null;
};

type TabKey = "my_cards" | "participating";
type ViewMode = "grid" | "stack";

const VIEW_MODE_STORAGE_KEY = "dashboard:viewMode";

const readStoredViewMode = (): ViewMode => {
  if (typeof window === "undefined") return "grid";
  const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
  return stored === "stack" ? "stack" : "grid";
};

const Dashboard = () => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>("my_cards");
  const currency = useCurrency();
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [occasionFilters, setOccasionFilters] = useState<string[]>([]);
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("status");
  const [viewMode, setViewMode] = useState<ViewMode>(readStoredViewMode);
  // Stack view peel cycle. Each peel increments the rotation so the
  // stack order wraps cleanly without accumulating duplicates when
  // the user cycles past the first card.
  const [cycleCount, setCycleCount] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
  }, [viewMode]);
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Draft delete state — separate from the main tile click handler so
  // clicking the trash icon doesn't also navigate into the draft's
  // resume flow. The AlertDialog is rendered once at the page level
  // and parameterised by the draft we're about to delete.
  const [draftToDelete, setDraftToDelete] = useState<DashboardCollection | null>(null);
  const [deletingDraft, setDeletingDraft] = useState(false);

  const confirmDeleteDraft = async () => {
    if (!draftToDelete) return;
    setDeletingDraft(true);
    const { error } = await deleteCollection(draftToDelete.id);
    setDeletingDraft(false);
    if (error) {
      toast({
        variant: "destructive",
        title: t("common.error", { defaultValue: "Something went wrong" }),
        description: error.message,
      });
      return;
    }
    // Invalidate the dashboard query so the deleted draft disappears
    // without a full refetch dance — react-query refetches in the
    // background and drops the deleted row.
    queryClient.invalidateQueries({ queryKey: ["dashboard", "collections"] });
    toast({
      title: t("dashboard.draftDeleted", { defaultValue: "Draft deleted" }),
    });
    setDraftToDelete(null);
  };

  const toggleStatus = (value: string) =>
    setStatusFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  const toggleOccasion = (value: string) =>
    setOccasionFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  const toggleType = (value: string) =>
    setTypeFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [user, loading, navigate]);

  // Dashboard cards fetch — migrated to react-query so switching tabs
  // or bouncing back from a card detail page uses the in-memory cache
  // instead of re-issuing the 2+ Supabase round trips every time.
  // Cache TTL is governed by the global defaults in App.tsx (30s
  // stale, 5min gc), which means the list stays instant for the
  // typical "go look at a card, come back" navigation pattern.
  const {
    data: dashboardData,
    isLoading: loadingData,
    isError: fetchError,
  } = useQuery({
    queryKey: ["dashboard", "collections", user?.id, user?.email],
    enabled: !!user,
    queryFn: async () => {
      const [ownResult, participatingResult] = await Promise.all([
        getCollectionsByOrganizer(user!.id),
        getParticipatingCollections(user!.email || ""),
      ]);
      if (ownResult.error) throw ownResult.error;

      const ownRows = ownResult.data || [];
      const ownIds = ownRows.map((c) => c.id);
      // My Cards tiles don't render the signer count, so we skip the
      // message count fetch for owned collections entirely — it was a
      // full extra round trip blocking the list render.
      const myCards: DashboardCollection[] = ownRows.map(({ card_templates, ...rest }) => ({
        ...rest,
        template_image_url: card_templates?.image_url ?? null,
      }));

      const ownIdSet = new Set(ownIds);
      const participatingRows = (participatingResult.data || []).filter((c) => !ownIdSet.has(c.id));
      const partIds = participatingRows.map((c) => c.id);
      const partCounts = partIds.length > 0 ? await getMessageCountsForCollections(partIds) : new Map();
      const participating: DashboardCollection[] = participatingRows.map(({ card_templates, ...rest }) => ({
        ...rest,
        message_count: partCounts.get(rest.id) || 0,
        template_image_url: card_templates?.image_url ?? null,
      }));

      return { myCards, participating };
    },
  });

  const myCards = dashboardData?.myCards ?? [];
  const participating = dashboardData?.participating ?? [];

  const sourceCollections = activeTab === "my_cards" ? myCards : participating;
  const isParticipating = activeTab === "participating";

  const collections = sourceCollections.filter((c) => {
    if (statusFilters.length > 0 && !statusFilters.includes(c.status || "")) return false;
    if (occasionFilters.length > 0 && !occasionFilters.includes(c.occasion || "")) return false;
    if (typeFilters.length > 0 && !typeFilters.includes(c.type || "")) return false;
    return true;
  });

  // Reset the peel cycle when the visible cards change (filter, tab
  // switch, deletion) so the rotation doesn't drift past the new
  // length.
  const collectionsKey = collections.map((c) => c.id).join(",");
  useEffect(() => {
    setCycleCount(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionsKey]);

  // Stack display order: rotate the collections by cycleCount so the
  // cycle wraps cleanly. The card that was on top N peels ago is N
  // slots deeper now, and after collections.length peels we're back
  // at the original order without re-mounting any tiles.
  const stackCollections = (() => {
    if (collections.length === 0) return collections;
    const offset = ((cycleCount % collections.length) + collections.length) % collections.length;
    if (offset === 0) return collections;
    return [...collections.slice(offset), ...collections.slice(0, offset)];
  })();

  const hasActiveFilters =
    statusFilters.length > 0 || occasionFilters.length > 0 || typeFilters.length > 0;
  const activeFilterCount =
    statusFilters.length + occasionFilters.length + typeFilters.length;
  const clearFilters = () => {
    setStatusFilters([]);
    setOccasionFilters([]);
    setTypeFilters([]);
  };

  const filterPanel = (
    <div className="flex flex-col">
      {/* Selected chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pb-4 mb-4 border-b border-border">
          {statusFilters.map((s) => (
            <button
              key={`chip-status-${s}`}
              onClick={() => toggleStatus(s)}
              className="h-7 ps-3 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
            >
              <span>{t(`dashboard.status.${s}`)}</span>
              <X size={12} />
            </button>
          ))}
          {occasionFilters.map((o) => {
            const opt = OCCASION_OPTIONS.find((x) => x.value === o);
            if (!opt) return null;
            return (
              <button
                key={`chip-occ-${o}`}
                onClick={() => toggleOccasion(o)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`occasions.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
          {typeFilters.map((tp) => {
            const opt = TYPE_FILTER_OPTIONS.find((x) => x.value === tp);
            if (!opt) return null;
            return (
              <button
                key={`chip-type-${tp}`}
                onClick={() => toggleType(tp)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`dashboard.type.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
        </div>
      )}

      {/* Two-column body */}
      <div className="flex border border-border rounded-2xl overflow-hidden h-[50vh] sm:h-[55vh] max-h-[420px]">
        {/* Categories (left) */}
        <div className="w-32 sm:w-40 shrink-0 bg-muted/30 border-e border-border overflow-y-auto">
          <button
            onClick={() => setActiveCategory("status")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "status"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.status", { defaultValue: "Status" })}</span>
            {statusFilters.length > 0 && (
              <span className="ms-1 text-primary">({statusFilters.length})</span>
            )}
          </button>
          <button
            onClick={() => setActiveCategory("occasion")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "occasion"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.occasion", { defaultValue: "Occasion" })}</span>
            {occasionFilters.length > 0 && (
              <span className="ms-1 text-primary">({occasionFilters.length})</span>
            )}
          </button>
          <button
            onClick={() => setActiveCategory("type")}
            className={`w-full text-start px-4 py-3.5 text-sm font-medium transition-colors border-s-2 ${
              activeCategory === "type"
                ? "bg-background text-foreground border-primary"
                : "text-muted-foreground hover:text-foreground border-transparent"
            }`}
          >
            <span>{t("dashboard.filter.type", { defaultValue: "Type" })}</span>
            {typeFilters.length > 0 && (
              <span className="ms-1 text-primary">({typeFilters.length})</span>
            )}
          </button>
        </div>

        {/* Options (right) */}
        <div className="flex-1 overflow-y-auto p-2">
          {activeCategory === "status" ? (
            <div className="space-y-1">
              {STATUS_FILTER_OPTIONS.map((opt) => {
                const checked = statusFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleStatus(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="flex-1 truncate">{t(`dashboard.status.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          ) : activeCategory === "occasion" ? (
            <div className="space-y-1">
              {OCCASION_OPTIONS.map((opt) => {
                const checked = occasionFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleOccasion(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="text-base leading-none">{opt.emoji}</span>
                    <span className="flex-1 truncate">{t(`occasions.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1">
              {TYPE_FILTER_OPTIONS.map((opt) => {
                const checked = typeFilters.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    onClick={() => toggleType(opt.value)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
                      checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                        checked
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {checked && <Check size={12} strokeWidth={3} />}
                    </span>
                    <span className="text-base leading-none">{opt.emoji}</span>
                    <span className="flex-1 truncate">{t(`dashboard.type.${opt.key}`)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 mt-5">
        <Button
          variant="outline"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="rounded-full flex-1"
        >
          {t("dashboard.filter.clearFilter", { defaultValue: "Clear filter" })}
        </Button>
        <Button
          onClick={() => setFilterOpen(false)}
          className="rounded-full flex-1"
        >
          {t("dashboard.filter.apply", { defaultValue: "Apply" })}
        </Button>
      </div>
    </div>
  );

  // Desktop sidebar variant: same data as filterPanel, but rendered as
  // collapsible <details> sections so users can expand multiple groups
  // at once. Stays vertical to fit the right-side Sheet.
  const renderOptionRow = (
    checked: boolean,
    onToggle: () => void,
    label: string,
    emoji?: string,
    key?: string,
  ) => (
    <button
      key={key}
      onClick={onToggle}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-start transition-colors ${
        checked ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
      }`}
    >
      <span
        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
          checked ? "bg-primary border-primary text-primary-foreground" : "border-border"
        }`}
      >
        {checked && <Check size={12} strokeWidth={3} />}
      </span>
      {emoji && <span className="text-base leading-none">{emoji}</span>}
      <span className="flex-1 truncate">{label}</span>
    </button>
  );

  const desktopFilterPanel = (
    <div className="flex flex-col h-full">
      {/* Selected chips */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 pb-4 mb-4 border-b border-border">
          {statusFilters.map((s) => (
            <button
              key={`d-chip-status-${s}`}
              onClick={() => toggleStatus(s)}
              className="h-7 ps-3 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
            >
              <span>{t(`dashboard.status.${s}`)}</span>
              <X size={12} />
            </button>
          ))}
          {occasionFilters.map((o) => {
            const opt = OCCASION_OPTIONS.find((x) => x.value === o);
            if (!opt) return null;
            return (
              <button
                key={`d-chip-occ-${o}`}
                onClick={() => toggleOccasion(o)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`occasions.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
          {typeFilters.map((tp) => {
            const opt = TYPE_FILTER_OPTIONS.find((x) => x.value === tp);
            if (!opt) return null;
            return (
              <button
                key={`d-chip-type-${tp}`}
                onClick={() => toggleType(tp)}
                className="h-7 ps-2.5 pe-2 rounded-full bg-primary/10 text-primary text-xs font-medium flex items-center gap-1.5 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm leading-none">{opt.emoji}</span>
                <span>{t(`dashboard.type.${opt.key}`)}</span>
                <X size={12} />
              </button>
            );
          })}
        </div>
      )}

      {/* Collapsible groups. Native <details> keeps state per-section
          and lets multiple groups stay open at once. */}
      <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-2">
        <details open className="group rounded-xl border border-border">
          <summary className="flex items-center justify-between cursor-pointer list-none px-4 py-3 text-sm font-medium select-none">
            <span className="flex items-center gap-2">
              {t("dashboard.filter.status", { defaultValue: "Status" })}
              {statusFilters.length > 0 && (
                <span className="text-xs text-primary">({statusFilters.length})</span>
              )}
            </span>
            <ChevronDown size={16} className="transition-transform group-open:rotate-180 text-muted-foreground" />
          </summary>
          <div className="px-2 pb-2 space-y-1">
            {STATUS_FILTER_OPTIONS.map((opt) =>
              renderOptionRow(
                statusFilters.includes(opt.value),
                () => toggleStatus(opt.value),
                t(`dashboard.status.${opt.key}`),
                undefined,
                `d-status-${opt.value}`,
              ),
            )}
          </div>
        </details>

        <details open className="group rounded-xl border border-border">
          <summary className="flex items-center justify-between cursor-pointer list-none px-4 py-3 text-sm font-medium select-none">
            <span className="flex items-center gap-2">
              {t("dashboard.filter.occasion", { defaultValue: "Occasion" })}
              {occasionFilters.length > 0 && (
                <span className="text-xs text-primary">({occasionFilters.length})</span>
              )}
            </span>
            <ChevronDown size={16} className="transition-transform group-open:rotate-180 text-muted-foreground" />
          </summary>
          <div className="px-2 pb-2 space-y-1">
            {OCCASION_OPTIONS.map((opt) =>
              renderOptionRow(
                occasionFilters.includes(opt.value),
                () => toggleOccasion(opt.value),
                t(`occasions.${opt.key}`),
                opt.emoji,
                `d-occ-${opt.value}`,
              ),
            )}
          </div>
        </details>

        <details open className="group rounded-xl border border-border">
          <summary className="flex items-center justify-between cursor-pointer list-none px-4 py-3 text-sm font-medium select-none">
            <span className="flex items-center gap-2">
              {t("dashboard.filter.type", { defaultValue: "Type" })}
              {typeFilters.length > 0 && (
                <span className="text-xs text-primary">({typeFilters.length})</span>
              )}
            </span>
            <ChevronDown size={16} className="transition-transform group-open:rotate-180 text-muted-foreground" />
          </summary>
          <div className="px-2 pb-2 space-y-1">
            {TYPE_FILTER_OPTIONS.map((opt) =>
              renderOptionRow(
                typeFilters.includes(opt.value),
                () => toggleType(opt.value),
                t(`dashboard.type.${opt.key}`),
                opt.emoji,
                `d-type-${opt.value}`,
              ),
            )}
          </div>
        </details>
      </div>

      {/* Footer */}
      <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className="rounded-full flex-1"
        >
          {t("dashboard.filter.clearFilter", { defaultValue: "Clear filter" })}
        </Button>
        <Button onClick={() => setFilterOpen(false)} className="rounded-full flex-1">
          {t("dashboard.filter.apply", { defaultValue: "Apply" })}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground font-sans">{t("dashboard.loading")}</div>
      </div>
    );
  }

  const renderNewCardButton = () => (
    <button
      key="new-card-button"
      type="button"
      onClick={() => navigate("/create")}
      aria-label={t("dashboard.newCard", { defaultValue: "New Card" })}
      className="bg-primary/5 rounded-2xl border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/10 transition-all duration-300 group flex items-center justify-center"
    >
      <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
        <Plus size={28} strokeWidth={2.5} />
      </div>
    </button>
  );

  // Click target for a card tile. Kept outside the grid/stack render
  // paths so both views open the same destination for a given card.
  const openCollection = (c: DashboardCollection) => {
    const isDraft = c.status === "draft";
    if (isParticipating) {
      navigate(`/card/${c.share_slug}`);
    } else if (isDraft) {
      navigate(`/create?draft=${c.id}`);
    } else {
      navigate(`/card/${c.share_slug}/manage`, { state: { fromDashboard: true } });
    }
  };

  // Inner card contents (image area + body). The outer wrapper differs
  // per view (plain grid div vs. absolutely-positioned stack tile) so
  // the wrapper's className/onClick/animation props are applied by the
  // caller.
  const renderTileContent = (c: DashboardCollection) => {
    const collected = Number(c.total_collected || 0);
    const signerCount = c.message_count || 0;
    const isDraft = c.status === "draft";
    return (
      <>
        <div className="relative aspect-[3/4] p-1 overflow-hidden">
          {c.product_image || c.template_image_url ? (
            <>
              {c.product_image ? (
                <img
                  src={c.product_image}
                  alt={c.product_name || c.title || c.recipient_name || "Product"}
                  loading="lazy"
                  decoding="async"
                  className={`absolute inset-1 w-[calc(100%-0.5rem)] h-[calc(100%-0.5rem)] object-cover rounded-sm transition-opacity duration-300 ${
                    c.template_image_url ? "group-hover:opacity-0" : ""
                  }`}
                />
              ) : null}
              {c.template_image_url ? (
                <img
                  src={c.template_image_url}
                  alt={c.title || c.recipient_name || "Card"}
                  loading="lazy"
                  decoding="async"
                  className={`absolute inset-1 w-[calc(100%-0.5rem)] h-[calc(100%-0.5rem)] object-cover rounded-sm transition-opacity duration-300 ${
                    c.product_image ? "opacity-0 group-hover:opacity-100" : ""
                  }`}
                />
              ) : null}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-secondary rounded-sm">
              <span className="text-4xl font-serif font-extralight text-muted-foreground/40">{c.recipient_name?.[0]?.toUpperCase() || "?"}</span>
            </div>
          )}
          {isDraft && (
            <button
              type="button"
              aria-label={t("dashboard.deleteDraft", { defaultValue: "Delete draft" })}
              onClick={(e) => {
                e.stopPropagation();
                setDraftToDelete(c);
              }}
              className="absolute top-2.5 end-2.5 h-7 w-7 rounded-full bg-background/95 backdrop-blur-sm flex items-center justify-center text-muted-foreground shadow-sm transition-all sm:opacity-0 sm:group-hover:opacity-100 hover:text-destructive hover:scale-105"
            >
              <Trash2 size={14} />
            </button>
          )}
          {collected > 0 && (
            <div className="absolute bottom-2.5 start-2.5 flex items-center gap-1 bg-background/95 backdrop-blur-sm rounded-full px-2.5 py-1 shadow-sm">
              <CircleDollarSign size={12} className="text-primary" />
              <span className="text-[11px] font-semibold text-foreground font-sans">{currency} {collected.toFixed(0)}</span>
            </div>
          )}
        </div>
        <div className="px-3.5 py-3 space-y-1">
          <h3 className="text-sm font-semibold text-foreground truncate leading-tight">
            {c.title || c.recipient_name || t("dashboard.untitledDraft", { defaultValue: "Untitled draft" })}
          </h3>
          {c.pool_type && (
            <span className="block text-[10px] text-muted-foreground font-sans truncate">
              {t(`dashboard.poolType.${c.pool_type}`, {
                defaultValue:
                  c.pool_type === "product"
                    ? "Product pool"
                    : c.pool_type === "sign_only"
                      ? "Sign only"
                      : "Money pool",
              })}
            </span>
          )}
          {isDraft && (
            <span className="inline-flex items-center rounded-md border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-primary">
              {t("dashboard.status.draft", { defaultValue: "Draft" })}
            </span>
          )}
          {!isDraft && (
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground font-sans">
                {isParticipating
                  ? `${signerCount} ${signerCount === 1 ? t("dashboard.signer") : t("dashboard.signers")}`
                  : t(`dashboard.status.${c.status}`)}
              </span>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* On desktop the in-grid "New card" tile is the primary create
          affordance, so we hide the redundant header + button there.
          Mobile still shows it because the in-grid tile is below the
          fold. */}
      <AppHeader
        userMenuVariant="dashboard"
        actions={
          <button
            onClick={() => navigate("/create")}
            className={`w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground hover:text-foreground ${
              viewMode === "stack" ? "" : "sm:hidden"
            }`}
          >
            <Plus size={18} />
          </button>
        }
      />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Welcome greeting */}
        <div className="mb-4">
          <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
            {(() => {
              const displayName =
                profile?.display_name?.trim() ||
                (user?.email ? user.email.split("@")[0] : "");
              return displayName
                ? t("dashboard.welcomeWithName", {
                    name: displayName,
                    defaultValue: `Welcome, ${displayName}`,
                  })
                : t("dashboard.welcome", { defaultValue: "Welcome" });
            })()}
          </h1>
        </div>

        {/* Tabs + toolbar.
            On mobile: tabs on their own row, toolbar below.
            On desktop (sm+): everything on one row — tabs left, then
            filter + view toggle + count pushed to the right. */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
          {/* Tabs */}
          <div className="flex w-full sm:w-fit gap-1 bg-muted rounded-full p-1">
            <button
              onClick={() => setActiveTab("my_cards")}
              className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === "my_cards"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("dashboard.myCards", { defaultValue: "My Cards" })}
            </button>
            <button
              onClick={() => setActiveTab("participating")}
              className={`flex-1 sm:flex-none px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === "participating"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t("dashboard.participating", { defaultValue: "Participating" })}
              {participating.length > 0 && (
                <span className="ms-1.5 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                  {participating.length}
                </span>
              )}
            </button>
          </div>

          {/* Right cluster on desktop: filter + view toggle + count.
              On mobile this wraps below the tabs as its own row. */}
          <div className="flex items-center gap-2 sm:ms-auto">
            <span className="me-auto sm:me-1 text-xs text-muted-foreground font-sans">
              {t("dashboard.cardCount", {
                count: collections.length,
                defaultValue: `${collections.length} ${collections.length === 1 ? "card" : "cards"}`,
              })}
            </span>

            {/* Filter button */}
            <button
              onClick={() => setFilterOpen(true)}
              aria-label={t("dashboard.filter.button", { defaultValue: "Filter" })}
              className={`relative h-10 px-4 rounded-full border text-sm font-medium flex items-center gap-2 transition-colors ${
                hasActiveFilters
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              }`}
            >
              <SlidersHorizontal size={15} />
              <span className="hidden sm:inline">
                {t("dashboard.filter.button", { defaultValue: "Filter" })}
              </span>
              {hasActiveFilters && (
                <span className="min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Grid / Stack view toggle */}
            <div className="h-10 flex items-center gap-1 rounded-full border border-border p-0.5">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                aria-label={t("dashboard.view.grid", { defaultValue: "Grid view" })}
                aria-pressed={viewMode === "grid"}
                className={`h-full aspect-square rounded-full flex items-center justify-center transition-colors ${
                  viewMode === "grid"
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <LayoutGrid size={15} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("stack")}
                aria-label={t("dashboard.view.stack", { defaultValue: "Stack view" })}
                aria-pressed={viewMode === "stack"}
                className={`h-full aspect-square rounded-full flex items-center justify-center transition-colors ${
                  viewMode === "stack"
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Layers size={15} />
              </button>
            </div>
          </div>
        </div>

        {/* Filter UI: bottom sheet on mobile, right-side sidebar Sheet on desktop */}
        {isMobile ? (
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetContent
              side="bottom"
              className="rounded-t-3xl max-h-[85vh] overflow-y-auto p-6"
            >
              <SheetHeader className="mb-4">
                <SheetTitle className="text-start">
                  {t("dashboard.filter.title", { defaultValue: "Filters" })}
                </SheetTitle>
              </SheetHeader>
              {filterPanel}
            </SheetContent>
          </Sheet>
        ) : (
          <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
            <SheetContent
              side="right"
              className="w-full sm:max-w-md p-6 flex flex-col"
            >
              <SheetHeader className="mb-4">
                <SheetTitle className="text-start">
                  {t("dashboard.filter.title", { defaultValue: "Filters" })}
                </SheetTitle>
              </SheetHeader>
              {desktopFilterPanel}
            </SheetContent>
          </Sheet>
        )}

        {fetchError ? (
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-foreground mb-2">{t("dashboard.somethingWrong")}</h2>
            <p className="text-muted-foreground mb-6">{t("dashboard.couldntLoad")}</p>
            <Button onClick={() => window.location.reload()} className="rounded-full" size="lg">
              {t("dashboard.retry")}
            </Button>
          </div>
        ) : loadingData ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="bg-card rounded-2xl overflow-hidden border border-border/60"
              >
                <div className="aspect-[3/4] bg-muted animate-pulse" />
                <div className="px-3.5 py-3 space-y-1.5">
                  <div className="h-3.5 bg-muted rounded w-3/4 animate-pulse" />
                  <div className="h-2.5 bg-muted rounded w-1/2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          hasActiveFilters && sourceCollections.length > 0 ? (
            <motion.div
              key={`${activeTab}-filtered-empty`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {t("dashboard.filter.noMatches", { defaultValue: "No matching cards" })}
              </h2>
              <p className="text-muted-foreground font-sans mb-6">
                {t("dashboard.filter.noMatchesDesc", {
                  defaultValue: "Try changing or clearing your filters.",
                })}
              </p>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="rounded-full gap-2"
                size="lg"
              >
                <X size={16} /> {t("dashboard.filter.clear", { defaultValue: "Clear filters" })}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              {!isParticipating && <FlippingCardTypes />}
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {isParticipating
                  ? t("dashboard.noParticipating", { defaultValue: "No cards yet" })
                  : t("dashboard.noCardsYet")}
              </h2>
              <p className="text-muted-foreground font-sans mb-6">
                {isParticipating
                  ? t("dashboard.noParticipatingDesc", { defaultValue: "Cards you RSVP to, sign, or chip in to will appear here." })
                  : t("dashboard.noCardsDescription")}
              </p>
              {!isParticipating && (
                <Button onClick={() => navigate("/create")} className="rounded-full gap-2" size="lg">
                  <Plus size={18} /> {t("dashboard.createFirst")}
                </Button>
              )}
            </motion.div>
          )
        ) : viewMode === "stack" ? (
          <div className="relative flex items-center justify-center py-10 min-h-[60vh] sm:min-h-[70vh]">
            {stackCollections.map((c, i) => {
              // depth = 0 is the top of the pile. Deeper cards rest
              // further down and rotate slightly, capped so a large
              // deck doesn't fan out endlessly.
              const depth = i;
              const depthClamp = Math.min(depth, 18);
              const offsetY = depthClamp * 3;
              const offsetX = Math.sin(depth * 1.7) * 4;
              const rotation = Math.cos(depth * 0.9) * Math.min(depth * 0.6, 4);
              // Bottom card lands first, top card lands last — so the
              // pile visibly stacks from the ground up.
              const delay = (stackCollections.length - 1 - i) * 0.06;
              const isTop = depth === 0;
              return (
                // Outer wrapper owns stack positioning + entrance
                // animation. Keeping position here means drag on the
                // inner wrapper can snap cleanly back to its own local
                // origin without fighting with offset/rotation.
                <motion.div
                  key={c.id}
                  initial={{
                    y: -480,
                    x: 0,
                    rotate: depth % 2 === 0 ? -18 : 18,
                    opacity: 0,
                  }}
                  animate={{ y: offsetY, x: offsetX, rotate: rotation, opacity: 1 }}
                  transition={{
                    delay,
                    type: "spring",
                    stiffness: 90,
                    damping: 14,
                    mass: 0.7,
                  }}
                  style={{ zIndex: stackCollections.length - i }}
                  className="absolute"
                >
                  {/* Inner wrapper owns drag. dragSnapToOrigin returns
                      it to its local (0,0) so the outer's positioning
                      is always respected after release. */}
                  <motion.div
                    drag={isTop}
                    dragElastic={0.65}
                    dragMomentum={false}
                    dragSnapToOrigin
                    onDragEnd={(_event, info) => {
                      const magnitude = Math.hypot(info.offset.x, info.offset.y);
                      const velocity = Math.hypot(info.velocity.x, info.velocity.y);
                      if (magnitude > 120 || velocity > 500) {
                        setCycleCount((prev) => prev + 1);
                      }
                    }}
                    whileTap={isTop ? { scale: 1.01 } : undefined}
                    className={`w-[calc(100vw-6rem)] max-w-[16rem] sm:w-56 sm:max-w-none bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl border border-border/60 cursor-pointer group transition-shadow select-none ${
                      isTop ? "touch-none" : ""
                    }`}
                    onClick={() => openCollection(c)}
                  >
                    {renderTileContent(c)}
                  </motion.div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
            {/* New card button - only on My Cards tab, shown first */}
            {!isParticipating && renderNewCardButton()}

            {collections.map((c) => (
              <div
                key={c.id}
                className="bg-card rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer border border-border/60 hover:border-border"
                onClick={() => openCollection(c)}
              >
                {renderTileContent(c)}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Confirm dialog for deleting a draft. Rendered at the page
          level (not inside the tile map) so the AlertDialog portal
          mounts once per page render and the transition state lives
          outside the mapped tiles. */}
      <AlertDialog
        open={!!draftToDelete}
        onOpenChange={(open) => {
          if (!open && !deletingDraft) setDraftToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("dashboard.deleteDraftTitle", { defaultValue: "Delete this draft?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("dashboard.deleteDraftDesc", {
                defaultValue:
                  "The draft card will be permanently removed. This can't be undone.",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDraft}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteDraft();
              }}
              disabled={deletingDraft}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 gap-1.5"
            >
              {deletingDraft && <Loader2 size={14} className="animate-spin" />}
              {t("common.delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
